import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    ArrowLeft, Shield, Check, Users, Search,
    ChevronRight, Globe, Layers, UserCheck,
    Loader2, X, AlertCircle, Info,
} from 'lucide-react';
import { useAssignments } from '@/store/assignment';
import { useRoles } from '@/store/role';
import { useResources } from '@/store/resource';
import { useTenants } from '@/store/tenant';
import { useLayout } from '@/contexts/layout-context';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { http } from '@/libs/http';
import type { Role, Resource } from '@/types';

const STEPS = [
    { id: 1, label: 'Tenant' },
    { id: 2, label: 'Identities' },
    { id: 3, label: 'Scope' },
    { id: 4, label: 'Roles' },
];

export const CreateAssignmentPage = () => {
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();

    const [step, setStep] = useState(1);
    const [selectedTenantId, setSelectedTenantId] = useState('');
    const [selectedIdentityIds, setSelectedIdentityIds] = useState<string[]>([]);
    const [selectedResourceId, setSelectedResourceId] = useState('');
    const [identitySearch, setIdentitySearch] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingIdentities, setIsLoadingIdentities] = useState(false);
    const [isLoadingExisting, setIsLoadingExisting] = useState(false);
    const [roles, setRoles] = useState<Role[]>([]);
    const [resources, setResources] = useState<Resource[]>([]);
    const [tenantUsers, setTenantUsers] = useState<any[]>([]);
    const [tenantGroups, setTenantGroups] = useState<any[]>([]);
    const [checkedRoleIds, setCheckedRoleIds] = useState<Record<string, boolean>>({});

    const listTenants = useTenants(s => s.list);
    const tenants = useTenants(s => s.tenants);
    const listTenantUsers = useTenants(s => s.listUsers);
    const listTenantGroups = useTenants(s => s.listGroups);
    const createAssignment = useAssignments(s => s.create);
    const deleteAssignment = useAssignments(s => s.delete);
    const listRoles = useRoles(s => s.list);
    const listResources = useResources(s => s.listResources);

    useEffect(() => {
        setBreadcrumbs([
            { name: 'Assignments', to: '/m/assignments' },
            { name: 'New Assignment' },
        ]);
        listTenants().catch(console.error);
        listRoles().then(d => setRoles(Array.isArray(d) ? d : [])).catch(console.error);
        listResources().then(d => setResources(Array.isArray(d) ? d : [])).catch(console.error);
    }, [setBreadcrumbs, listTenants, listRoles, listResources]);

    useEffect(() => {
        if (!selectedTenantId) {
            setTenantUsers([]); setTenantGroups([]);
            setSelectedIdentityIds([]); setSelectedResourceId('');
            return;
        }
        setIsLoadingIdentities(true);
        setSelectedIdentityIds([]); setSelectedResourceId('');
        Promise.all([
            listTenantUsers(selectedTenantId).then(setTenantUsers).catch(() => []),
            listTenantGroups(selectedTenantId).then(setTenantGroups).catch(() => []),
        ]).finally(() => setIsLoadingIdentities(false));
    }, [selectedTenantId, listTenantUsers, listTenantGroups]);

    const selectedTenant = tenants.find(t => t.id === selectedTenantId);

    const tenantScopeResources = selectedTenantId ? resources.filter(r => {
        if (!r.uri) return false;
        // Match 1: URI starts with or includes platform/tenants/<tenant_id> or platform/tenants/<tenant_slug>
        if (r.uri.includes(`tenants/${selectedTenantId}`) || r.uri.includes(`tenant/${selectedTenantId}`)) return true;
        if (selectedTenant?.slug && (r.uri.includes(`tenants/${selectedTenant.slug}`) || r.uri.includes(`tenant/${selectedTenant.slug}`))) return true;
        // Match 2: Enriched metadata has matching tenant_id
        if (r.metadata?.tenant_id === selectedTenantId) return true;
        // Match 3: Parent resource is tenant ID or root resource
        if (r.parent_id === selectedTenantId) return true;
        return false;
    }) : resources;

    const rootResource = tenantScopeResources.find(r =>
        r.uri === `platform/tenants/${selectedTenantId}` ||
        r.uri === `platform/tenant/${selectedTenantId}` ||
        (selectedTenant?.slug && (r.uri === `platform/tenants/${selectedTenant.slug}` || r.uri === `platform/tenant/${selectedTenant.slug}`)) ||
        (r.kind_id === 'platform::tenant' && r.metadata?.tenant_id === selectedTenantId)
    );

    const tenantRootValue = rootResource?.id || `tenant-root-${selectedTenantId}`;
    const tenantRootUri = rootResource?.uri || (selectedTenant?.slug ? `platform/tenants/${selectedTenant.slug}` : `platform/tenants/${selectedTenantId}`);

    const rawSelectedResource = resources.find(r => r.id === selectedResourceId);
    const selectedResource = rawSelectedResource ? {
        ...rawSelectedResource,
        uri: (selectedTenantId && (rawSelectedResource.kind_id?.includes('application') || rawSelectedResource.metadata?.application_id || rawSelectedResource.uri.includes('/applications/')))
            ? `platform/tenants/${selectedTenantId}/applications/${rawSelectedResource.metadata?.application_id || rawSelectedResource.uri.split('/').pop() || rawSelectedResource.id}`
            : rawSelectedResource.uri
    } : (
        selectedResourceId && selectedResourceId.startsWith('tenant-root-') ? {
            id: selectedResourceId,
            kind_id: 'platform::tenant',
            uri: tenantRootUri,
            metadata: { tenant_id: selectedTenantId, tenant_name: selectedTenant?.name }
        } as any : undefined
    );

    const tenantIdentities = [
        ...tenantUsers.map(u => ({ id: u.identity_id || u.id, name: u.name || u.email, email: u.email || '', type: 'user' as const })),
        ...tenantGroups.map(g => ({ id: g.identity_id || g.id, name: g.name, email: g.email || '', type: 'group' as const })),
    ];

    const filteredIdentities = tenantIdentities.filter(i => {
        const q = identitySearch.toLowerCase();
        return i.name?.toLowerCase().includes(q) || i.email?.toLowerCase().includes(q) || i.id?.toLowerCase().includes(q);
    });

    const scopeOptions = [
        {
            value: tenantRootValue,
            label: selectedTenant ? `${selectedTenant.name} (Tenant Root)` : 'Tenant Root',
            sublabel: tenantRootUri,
            logo: selectedTenant?.logo_url || rootResource?.metadata?.tenant_logo,
        },
        ...tenantScopeResources
            .filter(r => r.id !== rootResource?.id && r.uri !== tenantRootUri)
            .map(res => {
                let instanceUri = res.uri;
                if (selectedTenantId && (res.kind_id?.includes('application') || res.metadata?.application_id || res.uri.includes('/applications/'))) {
                    const appId = res.metadata?.application_id || res.uri.split('/').pop() || res.id;
                    instanceUri = `platform/tenants/${selectedTenantId}/applications/${appId}`;
                }
                return {
                    value: res.id,
                    label: res.metadata?.application_name ?? res.kind_name ?? res.uri,
                    sublabel: instanceUri,
                    logo: res.metadata?.application_logo || res.metadata?.tenant_logo || undefined,
                };
            }),
    ];

    const filteredRoles = selectedResource
        ? roles.filter(r => {
            // Generic role not tagged to anything
            if (!r.applicable_scopes || r.applicable_scopes.length === 0) return true;

            const targetKind = (selectedResource.kind_id || '').toLowerCase();
            const targetUri = (selectedResource.uri || '').toLowerCase();

            return r.applicable_scopes.some(s => {
                const scopeLower = s.toLowerCase();
                // Generic role tagged as "any"
                if (scopeLower === 'any') return true;

                // Exact match on resource kind (e.g. platform::application::abc)
                if (scopeLower === targetKind) return true;

                // Category match for platform applications, tenants, and idps
                if (scopeLower === 'platform::application' && (targetKind.startsWith('platform::application') || targetUri.includes('/applications/'))) {
                    return true;
                }
                if (scopeLower === 'platform::tenant' && (targetKind === 'platform::tenant' || targetUri.includes('/tenants/'))) {
                    return true;
                }
                if (scopeLower === 'platform::idp' && (targetKind.startsWith('platform::idp') || targetUri.includes('/idps/'))) {
                    return true;
                }

                return false;
            });
        })
        : [];

    const selectedResourceUri = selectedResource?.uri;
    const singleIdentityId = selectedIdentityIds.length === 1 ? selectedIdentityIds[0] : null;

    useEffect(() => {
        if (selectedResourceId && singleIdentityId && selectedResourceUri) {
            setIsLoadingExisting(true);
            http.get<any[]>(`/assignments?scope_id=${selectedResourceUri}&principal_id=${singleIdentityId}`)
                .then(data => {
                    const init: Record<string, boolean> = {};
                    (Array.isArray(data) ? data : []).forEach(a => (init[a.role_id] = true));
                    setCheckedRoleIds(init);
                })
                .catch(console.error)
                .finally(() => setIsLoadingExisting(false));
        } else {
            setCheckedRoleIds({});
        }
    }, [selectedResourceId, singleIdentityId, selectedResourceUri]);

    const toggleIdentity = (id: string) =>
        setSelectedIdentityIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const handleSave = async () => {
        if (!selectedResource) return;
        setIsSubmitting(true);
        try {
            const actions: Promise<any>[] = [];
            for (const principalId of selectedIdentityIds) {
                const io = tenantIdentities.find(i => i.id === principalId);
                const pt = (io?.type || 'user') as 'user' | 'group' | 'spn';
                let cur: any[] = [];
                try { cur = (await http.get<any[]>(`/assignments?scope_id=${selectedResource.uri}&principal_id=${principalId}`)) ?? []; } catch { }
                for (const role of filteredRoles) {
                    const checked = !!checkedRoleIds[role.id];
                    const existing = cur.find(a => a.role_id === role.id);
                    if (checked && !existing) actions.push(createAssignment({ role_id: role.id, scope_id: selectedResource.uri, principal_type: pt, principal_id: principalId }));
                    if (!checked && existing) actions.push(deleteAssignment(existing.id));
                }
            }
            if (actions.length === 0) { toast.info('No changes made'); navigate('/m/assignments'); return; }
            await Promise.all(actions);
            toast.success('Assignments updated');
            navigate('/m/assignments');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to update');
        } finally { setIsSubmitting(false); }
    };

    const canNext = () => {
        if (step === 1) return !!selectedTenantId;
        if (step === 2) return selectedIdentityIds.length > 0;
        if (step === 3) return !!selectedResourceId;
        return false;
    };

    const assignedCount = Object.values(checkedRoleIds).filter(Boolean).length;

    return (
        <div className="p-4 lg:p-8 max-w-4xl mx-auto w-full animate-in fade-in duration-500">
            <PageHeader
                title="Role Assignment"
                subtitle="Grant or revoke role access for identities within a tenant."
                actions={
                    <Button variant="ghost" onClick={() => navigate('/m/assignments')} className="gap-2">
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </Button>
                }
            />

            <div className="mt-8 animate-in slide-in-from-bottom-4 duration-500">
                <Card
                    title="Assignment Configuration"
                    subtitle="Step through tenant, identities, scope, and role selection."
                    className="glass"
                >
                    {/* ── Step indicator ── */}
                    <div className="flex items-center gap-1 mb-6">
                        {STEPS.map((s, idx) => {
                            const done = step > s.id;
                            const active = step === s.id;
                            return (
                                <div key={s.id} className="flex items-center flex-1 last:flex-none">
                                    <button
                                        type="button"
                                        onClick={() => { if (done) setStep(s.id); }}
                                        className={`flex items-center gap-1.5 ${done ? 'cursor-pointer' : 'cursor-default'}`}
                                    >
                                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold transition-all
                                            ${done ? 'bg-orange-500 text-white'
                                                : active ? 'ring-2 ring-orange-500 text-orange-500 bg-transparent'
                                                    : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'}`}>
                                            {done ? <Check className="w-3 h-3" /> : s.id}
                                        </div>
                                        <span className={`text-xs font-semibold hidden sm:block
                                            ${active ? 'text-slate-900 dark:text-white'
                                                : done ? 'text-orange-500'
                                                    : 'text-slate-400 dark:text-slate-500'}`}>
                                            {s.label}
                                        </span>
                                    </button>
                                    {idx < STEPS.length - 1 && (
                                        <div className={`flex-1 h-px mx-2 rounded-full transition-colors ${done ? 'bg-orange-400' : 'bg-slate-200 dark:bg-slate-700'}`} />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* ── Persistent selection summary ── */}
                    {(selectedTenantId || selectedIdentityIds.length > 0 || selectedResourceId || assignedCount > 0) && (
                        <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-lg text-xs">
                            {/* Tenant */}
                            <span className={`flex items-center gap-1.5 ${selectedTenantId ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 dark:text-slate-600'}`}>
                                <span className="font-semibold text-slate-500 dark:text-slate-400">Tenant</span>
                                {selectedTenantId
                                    ? <span className="font-bold text-slate-900 dark:text-white">{tenants.find(t => t.id === selectedTenantId)?.name}</span>
                                    : <span className="text-slate-400">—</span>
                                }
                            </span>
                            <span className="text-slate-200 dark:text-slate-700 select-none">|</span>
                            {/* Identities */}
                            <span className={`flex items-center gap-1.5 ${selectedIdentityIds.length > 0 ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 dark:text-slate-600'}`}>
                                <span className="font-semibold text-slate-500 dark:text-slate-400">Identities</span>
                                {selectedIdentityIds.length > 0
                                    ? <span className="font-bold text-orange-600 dark:text-orange-400">{selectedIdentityIds.length} selected</span>
                                    : <span className="text-slate-400">—</span>
                                }
                            </span>
                            <span className="text-slate-200 dark:text-slate-700 select-none">|</span>
                            {/* Scope */}
                            <span className={`flex items-center gap-1.5 ${selectedResourceId ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 dark:text-slate-600'}`}>
                                <span className="font-semibold text-slate-500 dark:text-slate-400">Scope</span>
                                {selectedResourceId
                                    ? <span className="font-bold text-slate-900 dark:text-white truncate max-w-36">{scopeOptions.find(o => o.value === selectedResourceId)?.label}</span>
                                    : <span className="text-slate-400">—</span>
                                }
                            </span>
                            <span className="text-slate-200 dark:text-slate-700 select-none">|</span>
                            {/* Roles */}
                            <span className={`flex items-center gap-1.5 ${assignedCount > 0 ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 dark:text-slate-600'}`}>
                                <span className="font-semibold text-slate-500 dark:text-slate-400">Roles</span>
                                {assignedCount > 0
                                    ? <span className="font-bold text-orange-600 dark:text-orange-400">{assignedCount} assigned</span>
                                    : <span className="text-slate-400">—</span>
                                }
                            </span>
                        </div>
                    )}

                    {/* ── STEP 1: Tenant ── */}
                    {step === 1 && (
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                                Select Tenant
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {tenants.map(tenant => {
                                    const sel = selectedTenantId === tenant.id;
                                    return (
                                        <div
                                            key={tenant.id}
                                            onClick={() => setSelectedTenantId(tenant.id)}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all
                                                ${sel
                                                    ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-500/10'
                                                    : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-slate-900'
                                                }`}
                                        >
                                            {tenant.logo_url
                                                ? <img src={tenant.logo_url} alt="" className="w-7 h-7 rounded-lg object-contain bg-slate-50 dark:bg-slate-800 p-0.5 shrink-0" />
                                                : <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0
                                                    ${sel ? 'bg-orange-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}>
                                                    {tenant.name?.[0]?.toUpperCase() ?? 'T'}
                                                </div>
                                            }
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{tenant.name}</div>
                                                <div className="text-[10px] text-slate-400 dark:text-slate-500 truncate font-mono mt-0.5">{tenant.slug}</div>
                                            </div>
                                            {sel && <Check className="w-4 h-4 text-orange-500 shrink-0" />}
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Choose the tenant context for this assignment.</p>
                        </div>
                    )}

                    {/* ── STEP 2: Identities ── */}
                    {step === 2 && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                                    Select Identities
                                </label>
                                {selectedIdentityIds.length > 0 && (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-600 dark:text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded-full">
                                        <UserCheck className="w-3 h-3" />{selectedIdentityIds.length} selected
                                    </span>
                                )}
                            </div>

                            {/* Search */}
                            <div className="relative">
                                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Search by name or email..."
                                    value={identitySearch}
                                    onChange={e => setIdentitySearch(e.target.value)}
                                    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg pl-10 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all text-slate-900 dark:text-white w-full"
                                />
                                {identitySearch && (
                                    <button onClick={() => setIdentitySearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>

                            {isLoadingIdentities ? (
                                <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
                                    <Loader2 className="w-4 h-4 animate-spin text-orange-500" />Loading identities...
                                </div>
                            ) : filteredIdentities.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-8 gap-2 text-slate-400">
                                    <Users className="w-6 h-6 opacity-40" />
                                    <span className="text-sm">No identities found</span>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto p-1 border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-50/50 dark:bg-slate-950/20">
                                    {filteredIdentities.some(i => i.type === 'user') && (
                                        <div className="col-span-full flex items-center gap-1.5 px-2 pt-1 pb-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                                            <Users className="w-3 h-3" />Users
                                        </div>
                                    )}
                                    {filteredIdentities.filter(i => i.type === 'user').map(ident => (
                                        <IdentityRow key={ident.id} ident={ident}
                                            checked={selectedIdentityIds.includes(ident.id)}
                                            onToggle={() => toggleIdentity(ident.id)} />
                                    ))}
                                    {filteredIdentities.some(i => i.type === 'group') && (
                                        <div className="col-span-full flex items-center gap-1.5 px-2 pt-2 pb-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                                            <Layers className="w-3 h-3" />Groups
                                        </div>
                                    )}
                                    {filteredIdentities.filter(i => i.type === 'group').map(ident => (
                                        <IdentityRow key={ident.id} ident={ident}
                                            checked={selectedIdentityIds.includes(ident.id)}
                                            onToggle={() => toggleIdentity(ident.id)} />
                                    ))}
                                </div>
                            )}
                            <p className="text-xs text-slate-500 dark:text-slate-400">Select one or more users or groups to assign roles to.</p>
                        </div>
                    )}

                    {/* ── STEP 3: Scope ── */}
                    {step === 3 && (
                        <div className="space-y-2">
                            <SearchableSelect
                                label="Scope / Resource"
                                placeholder="Select scope (Tenant Root or Application)..."
                                options={scopeOptions}
                                value={selectedResourceId}
                                onChange={setSelectedResourceId}
                            />
                            {selectedResourceId && selectedResource && (
                                <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-lg mt-2">
                                    <Globe className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                    <span className="text-[11px] text-slate-500 dark:text-slate-400 font-mono break-all">{selectedResource.uri}</span>
                                </div>
                            )}
                            {scopeOptions.length === 0 && (
                                <div className="p-6 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-lg text-slate-500 text-sm flex flex-col items-center gap-2">
                                    <Info className="w-5 h-5 text-slate-400" />
                                    <span>No scopes found for this tenant</span>
                                </div>
                            )}
                            <p className="text-xs text-slate-500 dark:text-slate-400">Choose the resource scope where the role assignment will apply.</p>
                        </div>
                    )}

                    {/* ── STEP 4: Roles ── */}
                    {step === 4 && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">
                                    Assign Roles
                                </label>
                                {assignedCount > 0 && (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-600 dark:text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded-full">
                                        <Shield className="w-3 h-3" />{assignedCount} assigned
                                    </span>
                                )}
                            </div>

                            {isLoadingExisting ? (
                                <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
                                    <Loader2 className="w-4 h-4 animate-spin text-orange-500" />Loading existing assignments...
                                </div>
                            ) : filteredRoles.length === 0 ? (
                                <div className="p-6 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-lg text-slate-500 text-sm flex flex-col items-center gap-2">
                                    <Info className="w-5 h-5 text-slate-400" />
                                    <span>No roles available for this scope</span>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto p-1 border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-50/50 dark:bg-slate-950/20">
                                    {filteredRoles.map(role => {
                                        const on = !!checkedRoleIds[role.id];
                                        return (
                                            <div
                                                key={role.id}
                                                onClick={() => setCheckedRoleIds(p => ({ ...p, [role.id]: !p[role.id] }))}
                                                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                                                    ${on
                                                        ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-500/10'
                                                        : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-slate-900'
                                                    }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={on}
                                                    onChange={() => { }}
                                                    className="w-4 h-4 rounded text-orange-500 focus:ring-orange-500 border-slate-300 dark:border-slate-700 mt-0.5 shrink-0"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <span className="text-sm font-bold text-slate-900 dark:text-white block truncate">{role.name}</span>
                                                    {role.applicable_scopes && role.applicable_scopes.length > 0 && (
                                                        <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono block truncate mt-0.5">
                                                            {role.applicable_scopes.slice(0, 2).join(', ')}
                                                        </span>
                                                    )}
                                                    {role.description && (
                                                        <span className="text-[10px] text-slate-600 dark:text-slate-400 block truncate mt-0.5">{role.description}</span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {selectedIdentityIds.length > 1 && (
                                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg">
                                    <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                                    <p className="text-xs text-amber-700 dark:text-amber-400">
                                        <strong>{selectedIdentityIds.length} identities</strong> selected — changes apply to all of them.
                                    </p>
                                </div>
                            )}
                            <p className="text-xs text-slate-500 dark:text-slate-400">Toggle roles on or off for the selected identities at this scope.</p>
                        </div>
                    )}

                    {/* ── Footer ── */}
                    <div className="pt-8 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between mt-6">
                        <Button
                            variant="ghost"
                            type="button"
                            onClick={() => step > 1 ? setStep(s => s - 1) : navigate('/m/assignments')}
                            className="gap-2"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {step > 1 ? 'Back' : 'Cancel'}
                        </Button>

                        {step < 4 ? (
                            <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()} className="gap-2 min-w-32">
                                Continue <ChevronRight className="w-4 h-4" />
                            </Button>
                        ) : (
                            <Button
                                onClick={handleSave}
                                isLoading={isSubmitting}
                                disabled={selectedIdentityIds.length === 0 || !selectedResourceId}
                                className="gap-2 min-w-40"
                            >
                                <Check className="w-4 h-4" />
                                Save Assignments
                            </Button>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
};

// ── Identity row sub-component ─────────────────────────────────────────────
interface IdentityRowProps {
    ident: { id: string; name: string; email: string; type: 'user' | 'group' };
    checked: boolean;
    onToggle: () => void;
}

const IdentityRow = ({ ident, checked, onToggle }: IdentityRowProps) => (
    <div
        onClick={onToggle}
        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
            ${checked
                ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-500/10'
                : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-slate-900'
            }`}
    >
        <input
            type="checkbox"
            checked={checked}
            onChange={() => { }}
            className="w-4 h-4 rounded text-orange-500 focus:ring-orange-500 border-slate-300 dark:border-slate-700 shrink-0"
        />
        <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold shrink-0
            ${ident.type === 'group'
                ? (checked ? 'bg-violet-500 text-white' : 'bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400')
                : (checked ? 'bg-orange-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300')
            }`}>
            {ident.name?.[0]?.toUpperCase() ?? '?'}
        </div>
        <div className="min-w-0 flex-1">
            <span className="text-sm font-bold text-slate-900 dark:text-white block truncate">{ident.name || ident.email}</span>
            {ident.email && <span className="text-[10px] text-slate-400 dark:text-slate-500 block truncate">{ident.email}</span>}
        </div>
    </div>
);
